// ProvisioningRequestPage — asking us to stand a platform up.
//
// The second of the three flows composed rather than ported. The design drop's
// nearest screen is its wizard step 3, "Set up your first instance", and this is
// deliberately NOT that screen: that one offers an environment toggle, a region
// dropdown and a version dropdown, then promises provisioning in about fifteen
// minutes. The environment toggle, the version dropdown and the fifteen minutes
// are still absent. `ProvisioningState`'s own docstring is explicit that
// standing a platform up is people's work today, so this page asks and says a
// person will act on it.
//
// A REGION DROPDOWN, though, is now here — and it did not come from the design
// drop. It came from the server: `commerce.regions` lists the regions the
// estate can genuinely deploy into and the form turned region into a choice, so
// the server started describing the field with a closed set of answers. This
// screen draws that set. Reading this page beside the design drop and
// concluding the wizard was ported after all would be reading it backwards.
//
// The FIELDS are not hard-coded. The server describes them from
// ProvisioningRequestForm — names, labels, whether each is required, whether it
// is a text area, and which answers it will accept if it only accepts some — so
// this screen asks for exactly what the server accepts and labels it exactly as
// the server-rendered page does. A label changed in Django changes here without
// an edit, and so does a region added to or dropped from the deployable list.
import { useCallback, useState, type FormEvent } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import { Input } from '@miq/design-system/components/ui/input';
import { Label } from '@miq/design-system/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@miq/design-system/components/ui/select';
import { Textarea } from '@miq/design-system/components/ui/textarea';

import { StateBadge } from '../components/StateBadge';
import { usePortalSession } from '../auth/context';
import {
  PortalDataFailed,
  type FieldDescriptor,
  fetchProvisioningForm,
  submitProvisioningRequest,
} from '../data/subscriptions';
import { useLoaded } from '../data/useLoaded';
import { useDocumentTitle } from '../../lib/documentTitle';

export interface ProvisioningRequestPageProps {
  publicId: string;
  onBack: () => void;
  /** Where a recorded request lands — the status page, as the template does. */
  onRecorded: () => void;
}

export function ProvisioningRequestPage({
  publicId,
  onBack,
  onRecorded,
}: ProvisioningRequestPageProps) {
  useDocumentTitle('Ask for a deployment');
  const { authorizedFetch } = usePortalSession();
  const read = useCallback(
    () => fetchProvisioningForm(authorizedFetch, publicId),
    [authorizedFetch, publicId],
  );
  const { state } = useLoaded(read);
  const fields = state.status === 'loaded' ? state.data.fields : [];

  const [values, setValues] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<Record<string, string[]>>({});
  const [refusal, setRefusal] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // What the form holds for a field: what the person entered, or — where the
  // server proposed an answer or offered a closed set — the answer it named.
  //
  // The default is DERIVED here rather than written into `values` when the form
  // arrives, so the value the control shows and the value the request carries
  // come out of ONE expression and cannot disagree. Seeding state instead needs
  // an effect that fires after the fetch resolves, and until it does the select
  // reads "US East" while `values` says nothing — the person touches no control
  // and sends a request with no region, and the only thing that comes back is a
  // required-field error against a field they can see is filled in.
  //
  // `initial` first, then the head of `choices`, and `||` rather than `??`
  // between them because a field that proposes nothing says so with an empty
  // string — a `??` would read that as an answer and leave the region select
  // showing its placeholder. Only `values` is allowed to be empty on purpose:
  // a person who clears the prefilled address has cleared it, and this must
  // not helpfully put it back while they are typing the colleague's.
  //
  // Taking `[0]` is not "any of them will do": commerce.regions orders the list
  // most-used first precisely so a form can show the head of it. Sorting these
  // for tidiness would throw that away.
  const valueOf = (field: FieldDescriptor) =>
    values[field.name] ?? (field.initial || field.choices[0]?.value) ?? '';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setRefusal('');
    setProblems({});
    setSubmitting(true);
    try {
      // Every described field goes up, not only the ones touched. An untouched
      // text field now sends '' where it used to send nothing, which Django
      // reads the same way, and that uniformity is what lets an untouched
      // select send the answer it is displaying.
      await submitProvisioningRequest(
        authorizedFetch,
        publicId,
        Object.fromEntries(fields.map((field) => [field.name, valueOf(field)])),
      );
      onRecorded();
    } catch (failure) {
      if (!(failure instanceof PortalDataFailed)) {
        throw failure;
      }
      setRefusal(failure.message);
      setProblems(failure.problems);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 p-6">
      <div>
        <Button variant="ghost" size="sm" className="-ml-2 mb-1" onPress={onBack}>
          <ArrowLeft className="size-3.5" /> Back to the subscription
        </Button>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Commerce · Deployment request
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          Ask us for a deployment
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Tell us where you need the platform, what to call it, and who should
          be able to log in first. Our team builds it and records here what came
          of it — this is a request a person acts on, not an automated
          provisioning pipeline.
        </p>
      </div>

      {state.status === 'loading' && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Reading the form…
          </CardContent>
        </Card>
      )}

      {state.status === 'refused' && (
        <Card>
          <CardContent className="py-8">
            <p role="alert" className="text-sm text-destructive">
              {state.refusal}
            </p>
          </CardContent>
        </Card>
      )}

      {state.status === 'loaded' && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5 text-base">
              {state.data.subscription.agreement_ref}
              <StateBadge subscription={state.data.subscription} />
            </CardTitle>
            <CardDescription>
              The deployment will run under this subscription
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} noValidate className="space-y-4">
              {fields.map((field) => {
                const id = `provisioning-${field.name}`;
                // The select is named by the visible label rather than by an
                // `aria-label` repeating it: react-aria builds the trigger's
                // name from `aria-labelledby` plus the chosen value, and an
                // `aria-label` would only be reached through a self-reference
                // that says the same word twice. `htmlFor` stays as well so
                // clicking the label still focuses the control.
                const labelId = `${id}-label`;
                const value = valueOf(field);
                const change = (next: string) =>
                  setValues((previous) => ({ ...previous, [field.name]: next }));
                return (
                  <div key={field.name} className="space-y-1.5">
                    <Label id={labelId} htmlFor={id}>
                      {field.label}
                      {!field.required && (
                        <span className="ml-1.5 font-normal text-muted-foreground">
                          (optional)
                        </span>
                      )}
                    </Label>
                    {/*
                      Choices first, because a closed set is a stronger fact
                      about a field than how tall its box is: the server will
                      refuse anything outside it, so a text box here would be
                      an invitation to be told no. `multiline` and `max_length`
                      only describe how to draw free text, and a field with
                      choices is not free text.
                    */}
                    {field.choices.length > 0 ? (
                      <Select aria-labelledby={labelId} value={value} onValueChange={change}>
                        <SelectTrigger id={id} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {field.choices.map((choice) => (
                            <SelectItem key={choice.value} value={choice.value}>
                              {choice.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.multiline ? (
                      <Textarea
                        id={id}
                        name={field.name}
                        rows={4}
                        value={value}
                        onChange={(event) => change(event.target.value)}
                      />
                    ) : (
                      <Input
                        id={id}
                        name={field.name}
                        maxLength={field.max_length ?? undefined}
                        value={value}
                        onChange={(event) => change(event.target.value)}
                      />
                    )}
                    {field.help_text && (
                      <p className="text-xs text-muted-foreground">{field.help_text}</p>
                    )}
                    {problems[field.name]?.map((problem) => (
                      <p key={problem} className="text-xs text-destructive">
                        {problem}
                      </p>
                    ))}
                  </div>
                );
              })}

              {refusal && (
                <p role="alert" className="text-sm text-destructive">
                  {refusal}
                </p>
              )}

              <div className="flex gap-2.5 pt-1">
                <Button variant="outline" onPress={onBack} isDisabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" isPending={submitting}>
                  {submitting ? 'Sending…' : 'Send the request'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                We will record the request straight away and it will show on the
                subscription as <em>requested</em> until somebody here has
                finished with it.
              </p>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
